
import LandingHero from "@/components/LandingHero";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Crown, Star, Image, BarChart3, MessageCircle } from "lucide-react";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import BabyAnimation from "@/components/animations/BabyAnimation";
import ChatbotButton from "@/components/chatbot/ChatbotButton";
import { useState } from "react";
import ChatbotDialog from "@/components/chatbot/ChatbotDialog";

const Index = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);

  return (
    <main className="min-h-screen w-full overflow-x-hidden">
      <Header />
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(var(--primary),0.15),transparent_50%)]" />
        
        <div className="container max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
            {t('index.hero.title', language)}
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('index.hero.subtitle', language)}
          </p>
        </div>
        
        {user && (
          <section className="py-12 md:py-16 w-full bg-accent/10">
            <div className="container max-w-5xl mx-auto px-4">
              <div className="glass-card rounded-xl p-4 sm:p-8 flex flex-col md:flex-row items-center gap-6 animate-fade-up">
                <div className="md:w-2/3">
                  <h2 className="text-xl sm:text-2xl font-bold mb-3">{t('index.welcomeBack.title', language)}</h2>
                  <p className="text-muted-foreground mb-4">
                    {t('index.welcomeBack.subtitle', language)}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => navigate('/generate')}>
                      {t('index.welcomeBack.createEntry', language)}
                    </Button>
                    <Button variant="outline" onClick={() => navigate('/entries')}>
                      {t('index.welcomeBack.viewEntries', language)}
                    </Button>
                  </div>
                </div>
                <div className="md:w-1/3 flex justify-center">
                  <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-primary/10 flex items-center justify-center">
                    <Star className="h-10 w-10 sm:h-12 sm:w-12 text-primary" />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        
        {/* New Chatbot Highlight Section */}
        <section className="py-12 md:py-16 w-full bg-gradient-to-r from-primary/5 to-secondary/5">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              <div className="md:w-1/2 order-2 md:order-1">
                <h2 className="text-2xl sm:text-3xl font-bold mb-3 animate-fade-up">
                  {t('index.chatbot.title', language)}
                </h2>
                <p className="text-muted-foreground mb-6 animate-fade-up animation-delay-100">
                  {t('index.chatbot.subtitle', language)}
                </p>
                <Button 
                  size="lg" 
                  onClick={() => setIsChatbotOpen(true)}
                  className="animate-fade-up animation-delay-200 group shadow-md hover:shadow-lg transition-all duration-300"
                >
                  <MessageCircle className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                  {t('index.chatbot.tryNow', language)}
                </Button>
              </div>
              <div className="md:w-1/2 order-1 md:order-2 flex justify-center mb-6 md:mb-0">
                <div className="relative h-60 w-60 sm:h-72 sm:w-72 rounded-full bg-primary/10 flex items-center justify-center animate-fade-up">
                  <img 
                    src="/lovable-uploads/a4693856-68b6-409f-8f78-84c4150495fd.png" 
                    alt="Nurse Amy" 
                    className="h-52 w-52 sm:h-64 sm:w-64 object-cover rounded-full border-4 border-primary/30 shadow-xl animate-pulse"
                    onError={(e) => {
                      e.currentTarget.src = 'https://api.dicebear.com/7.x/lorelei/svg?seed=nurse-amy';
                    }}
                  />
                  <div className="absolute -bottom-2 -right-2 sm:bottom-0 sm:right-0 bg-primary text-primary-foreground rounded-full p-3 shadow-lg animate-bounce">
                    <MessageCircle className="h-8 w-8" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        <ChatbotDialog open={isChatbotOpen} onOpenChange={setIsChatbotOpen} />
        
        {/* Add Baby Animation before Premium Benefits section */}
        <BabyAnimation />
        
        <section className="py-12 md:py-16 w-full">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold">
                {t('index.premiumBenefits.title', language)}
              </h2>
              <p className="text-muted-foreground mt-2 max-w-lg mx-auto px-2">
                {t('index.premiumBenefits.subtitle', language)}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {premiumBenefits.map((benefit, index) => (
                <div 
                  key={index} 
                  className="border rounded-xl p-4 sm:p-6 hover:border-primary/50 transition-colors duration-300 animate-fade-up" 
                  style={{ animationDelay: `${(index + 1) * 100}ms` }}
                >
                  <div className="mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3">{t(benefit.titleKey, language)}</h3>
                  <p className="text-muted-foreground text-sm mb-4">{t(benefit.descriptionKey, language)}</p>
                  <Separator className="my-3 sm:my-4" />
                  <p className="text-xs sm:text-sm font-medium">
                    {benefit.availability === 'premium' ? (
                      <span className="flex items-center text-amber-600">
                        <Crown className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1.5" />
                        {t('benefits.premiumFeature', language)}
                      </span>
                    ) : (
                      <span>{t('benefits.freeFeature', language)}</span>
                    )}
                  </p>
                </div>
              ))}
            </div>
            
            <div className="mt-8 md:mt-10 text-center">
              <Button 
                size="lg" 
                onClick={() => navigate('/premium')}
                className="px-6 sm:px-8"
              >
                <Crown className="mr-2 h-4 w-4" />
                {t('index.premiumBenefits.upgradeToPremium', language)}
              </Button>
            </div>
          </div>
        </section>
        
        <Footer />
      </section>
      
      {/* Enhance the chatbot button with animation */}
      <div className="fixed bottom-6 right-6 z-50 hover:scale-105 transition-transform duration-300 animate-bounce">
        <Button
          onClick={() => setIsChatbotOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg flex items-center justify-center bg-gradient-to-r from-primary to-primary/80"
          size="icon"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="sr-only">{t('chatbot.open', language)}</span>
        </Button>
      </div>
    </main>
  );
};

const premiumBenefits = [
  {
    titleKey: 'benefits.unlimitedEntries' as const,
    descriptionKey: 'benefits.unlimitedDescription' as const,
    availability: "premium",
    icon: <Star className="h-6 w-6 text-primary" />
  },
  {
    titleKey: 'benefits.growthStatistics' as const,
    descriptionKey: 'benefits.growthDescription' as const,
    availability: "free",
    icon: <BarChart3 className="h-6 w-6 text-primary" />
  },
  {
    titleKey: 'benefits.photoGallery' as const,
    descriptionKey: 'benefits.photoDescription' as const,
    availability: "premium",
    icon: <Image className="h-6 w-6 text-primary" />
  },
  {
    titleKey: 'benefits.basicEntry' as const,
    descriptionKey: 'benefits.basicDescription' as const,
    availability: "free",
    icon: <Star className="h-6 w-6 text-primary/60" />
  },
  {
    titleKey: 'benefits.advancedEntry' as const,
    descriptionKey: 'benefits.advancedDescription' as const,
    availability: "premium",
    icon: <Star className="h-6 w-6 text-primary" />
  },
  {
    titleKey: 'benefits.prioritySupport' as const,
    descriptionKey: 'benefits.supportDescription' as const,
    availability: "premium",
    icon: <Star className="h-6 w-6 text-primary" />
  }
];

export default Index;
